import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  serverTimestamp,
  runTransaction,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS, VALIDATION, LEARNING_STATUS, LOG_TYPES } from '../config/constants';
import { AppError, FirestoreError } from '../lib/errors';
import { closeCycle, incorporateClosedCycle } from '../lib/learning';

/**
 * Servicio de "grupos de alimentación" (feedingGroups).
 *
 * Stage 2: cada grupo tiene 0..N dispensers como array embebido. Reemplaza
 * el modelo previo de `dispenserStockGr/dispenserCapacityGr/usesDispenser`
 * escalares + flag. Convenciones:
 *  - dispensers.length === 0 → modo "sin dispenser" (la mascota come directo
 *    de la bolsa principal).
 *  - dispensers.length >= 1 → hay 1+ contenedores intermedios. Cuando hay
 *    varios, feedPet auto-selecciona el de mayor stock que cubra la porción.
 *  - Los ciclos de aprendizaje siguen en la raíz del grupo (no por dispenser):
 *    feedings de cualquier dispenser suman al mismo activeCycle.
 */

// =====================================================================
// HELPERS
// =====================================================================

/**
 * Genera un id único para un dispenser. Mismo patrón que generateCycleId.
 */
export const generateDispenserId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `disp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * Suma el stock de todos los dispensers (helper para UI / estimaciones).
 */
export const sumDispenserStock = (dispensers) =>
  (dispensers ?? []).reduce((acc, d) => acc + (d.stockGr ?? 0), 0);

/**
 * Suma la capacidad total de todos los dispensers.
 */
export const sumDispenserCapacity = (dispensers) =>
  (dispensers ?? []).reduce((acc, d) => acc + (d.capacityGr ?? 0), 0);

// =====================================================================
// VALIDADORES
// =====================================================================

const validateGroupName = (name) => {
  if (!name?.trim()) {
    throw new AppError('El nombre del grupo es requerido', 'INVALID_NAME');
  }
  if (name.length > VALIDATION.GROUP_NAME_MAX) {
    throw new AppError(
      `El nombre supera los ${VALIDATION.GROUP_NAME_MAX} caracteres`,
      'INVALID_NAME'
    );
  }
};

const validateBagCapacity = (bagCapacityGr) => {
  if (bagCapacityGr == null) return; // null es legítimo (no se setteó todavía)
  if (
    !Number.isInteger(bagCapacityGr) ||
    bagCapacityGr < VALIDATION.BAG_CAPACITY_MIN_GR ||
    bagCapacityGr > VALIDATION.BAG_CAPACITY_MAX_GR
  ) {
    throw new AppError(
      `La capacidad de la bolsa debe ser un entero entre ${VALIDATION.BAG_CAPACITY_MIN_GR} y ${VALIDATION.BAG_CAPACITY_MAX_GR} g`,
      'INVALID_BAG_CAPACITY'
    );
  }
};

/**
 * lowStockThresholdDays: int entre LOW_STOCK_THRESHOLD_DAYS_MIN y _MAX,
 * o null para que el grupo use el default global.
 */
const validateLowStockThresholdDays = (days) => {
  if (days == null) return; // null = usar default global (intencional)
  if (
    !Number.isInteger(days) ||
    days < VALIDATION.LOW_STOCK_THRESHOLD_DAYS_MIN ||
    days > VALIDATION.LOW_STOCK_THRESHOLD_DAYS_MAX
  ) {
    throw new AppError(
      `El umbral debe ser un entero entre ${VALIDATION.LOW_STOCK_THRESHOLD_DAYS_MIN} y ${VALIDATION.LOW_STOCK_THRESHOLD_DAYS_MAX} días`,
      'INVALID_LOW_STOCK_THRESHOLD'
    );
  }
};

/**
 * Valida un dispenser individual. Acepta stockGr opcional.
 */
const validateDispenser = ({ name, capacityGr, stockGr = 0 }) => {
  if (!name?.trim()) {
    throw new AppError('El nombre del dispenser es requerido', 'INVALID_DISPENSER_NAME');
  }
  if (name.length > VALIDATION.DISPENSER_NAME_MAX) {
    throw new AppError(
      `El nombre del dispenser supera los ${VALIDATION.DISPENSER_NAME_MAX} caracteres`,
      'INVALID_DISPENSER_NAME'
    );
  }
  if (
    !Number.isInteger(capacityGr) ||
    capacityGr < VALIDATION.DISPENSER_CAPACITY_MIN_GR ||
    capacityGr > VALIDATION.DISPENSER_CAPACITY_MAX_GR
  ) {
    throw new AppError(
      `La capacidad del dispenser debe ser un entero entre ${VALIDATION.DISPENSER_CAPACITY_MIN_GR} y ${VALIDATION.DISPENSER_CAPACITY_MAX_GR} g`,
      'INVALID_DISPENSER_CAPACITY'
    );
  }
  if (!Number.isInteger(stockGr) || stockGr < 0 || stockGr > capacityGr) {
    throw new AppError(
      'El stock del dispenser debe ser un entero entre 0 y su capacidad',
      'INVALID_DISPENSER_STOCK'
    );
  }
};

/**
 * Valida el array completo de dispensers (length cap + cada elemento).
 */
const validateDispensers = (dispensers) => {
  if (!Array.isArray(dispensers)) {
    throw new AppError('dispensers debe ser un array', 'INVALID_DISPENSERS');
  }
  if (dispensers.length > VALIDATION.MAX_DISPENSERS_PER_GROUP) {
    throw new AppError(
      `Máximo ${VALIDATION.MAX_DISPENSERS_PER_GROUP} dispensers por grupo`,
      'TOO_MANY_DISPENSERS'
    );
  }
  for (const d of dispensers) {
    validateDispenser(d);
  }
};

// =====================================================================
// CRUD
// =====================================================================

/**
 * Crea un grupo de alimentación para un hogar.
 *
 * dispensers: Array<{ name, capacityGr, stockGr? }> — opcional, default [].
 * El service asigna `id` a cada dispenser (crypto.randomUUID).
 *
 * Si dispensers es [], el grupo opera en "modo sin dispenser" — la mascota
 * come directo de mainStockGr.
 */
export const createFeedingGroup = async ({
  uid,
  familyId,
  name,
  bagCapacityGr = null,
  mainStockGr = 0,
  extraBagsCount = 0,
  dispensers = [],
}) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!familyId) throw new AppError('Hogar requerido', 'INVALID_INPUT');

  validateGroupName(name);
  validateBagCapacity(bagCapacityGr);
  validateDispensers(dispensers);

  if (
    !Number.isInteger(mainStockGr) ||
    mainStockGr < 0 ||
    mainStockGr > VALIDATION.MAIN_STOCK_MAX_GR
  ) {
    throw new AppError(
      `El stock principal debe ser un entero entre 0 y ${VALIDATION.MAIN_STOCK_MAX_GR}`,
      'INVALID_MAIN_STOCK'
    );
  }
  if (
    !Number.isInteger(extraBagsCount) ||
    extraBagsCount < 0 ||
    extraBagsCount > VALIDATION.EXTRA_BAGS_MAX
  ) {
    throw new AppError(
      `La cantidad de bolsas extra debe estar entre 0 y ${VALIDATION.EXTRA_BAGS_MAX}`,
      'INVALID_EXTRA_BAGS'
    );
  }

  // Generar ids para los dispensers de entrada y normalizar campos.
  const finalDispensers = dispensers.map((d) => ({
    id: generateDispenserId(),
    name: d.name.trim(),
    capacityGr: d.capacityGr,
    stockGr: d.stockGr ?? 0,
  }));

  try {
    const groupsRef = collection(db, COLLECTIONS.FEEDING_GROUPS);
    const docRef = await addDoc(groupsRef, {
      familyId,
      name: name.trim(),
      dispensers: finalDispensers,
      mainStockGr,
      bagCapacityGr,
      extraBagsCount,
      cycles: [],
      activeCycle: null,
      avgFeedingsPerRefill: null,
      avgRefillsPerBag: null,
      cyclesCompleted: 0,
      learningStatus: LEARNING_STATUS.LEARNING,
      // null = el grupo usa el default global (INVENTORY.DEFAULT_LOW_STOCK_THRESHOLD_DAYS)
      lowStockThresholdDays: null,
      deletedAt: null,
      createdBy: uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { groupId: docRef.id };
  } catch (err) {
    if (err instanceof AppError) throw err;
    throw new FirestoreError('No se pudo crear el grupo', err);
  }
};

/**
 * Actualiza campos editables de alto nivel del grupo.
 *
 * Campos editables aquí:
 *  - name
 *  - bagCapacityGr (bloqueado si extraBagsCount > 0)
 *  - lowStockThresholdDays (Fase 6: umbral para badge "Comprar pronto").
 *    Si es null, el grupo usa el default global (INVENTORY.DEFAULT_LOW_STOCK_THRESHOLD_DAYS).
 *
 * Lo que NO se edita acá:
 *  - dispensers: cada dispenser se gestiona con addDispenser / removeDispenser /
 *    renameDispenser / resizeDispenser (Stage 2B). Modificar el array entero
 *    aquí abriría side effects difíciles (qué hacer con ciclo, stock previo, etc.).
 *  - mainStockGr / extraBagsCount / cycles: gestionados por ops específicas.
 *  - familyId / createdBy / createdAt: bloqueados por reglas.
 */
export const updateFeedingGroup = async ({ uid, groupId, updates }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!groupId) throw new AppError('Grupo requerido', 'INVALID_INPUT');

  const ALLOWED = ['name', 'bagCapacityGr', 'lowStockThresholdDays'];
  const filtered = {};
  for (const key of Object.keys(updates || {})) {
    if (ALLOWED.includes(key)) {
      filtered[key] = updates[key];
    } else {
      throw new AppError(`Campo no editable: ${key}`, 'INVALID_FIELD');
    }
  }

  if (Object.keys(filtered).length === 0) {
    throw new AppError('No hay cambios para aplicar', 'NO_CHANGES');
  }

  if ('name' in filtered) {
    validateGroupName(filtered.name);
    filtered.name = filtered.name.trim();
  }
  if ('bagCapacityGr' in filtered) {
    validateBagCapacity(filtered.bagCapacityGr);
  }
  if ('lowStockThresholdDays' in filtered) {
    validateLowStockThresholdDays(filtered.lowStockThresholdDays);
  }

  try {
    const groupRef = doc(db, COLLECTIONS.FEEDING_GROUPS, groupId);
    const snap = await getDoc(groupRef);
    if (!snap.exists()) {
      throw new AppError('El grupo no existe', 'GROUP_NOT_FOUND');
    }

    // bagCapacityGr no se puede cambiar si hay bolsas en reserva
    // (mismo principio que addExtraBags: las bolsas viejas se mantienen con
    // su tamaño original).
    if ('bagCapacityGr' in filtered) {
      const current = snap.data();
      const currentCount = current.extraBagsCount ?? 0;
      if (
        current.bagCapacityGr != null &&
        filtered.bagCapacityGr != null &&
        filtered.bagCapacityGr !== current.bagCapacityGr &&
        currentCount > 0
      ) {
        throw new AppError(
          `Ya hay ${currentCount} bolsa${currentCount === 1 ? '' : 's'} en reserva del tamaño actual. Abrilas antes de cambiar el tamaño.`,
          'BAG_CAPACITY_MISMATCH'
        );
      }
    }

    await updateDoc(groupRef, {
      ...filtered,
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    throw new FirestoreError('No se pudo actualizar el grupo', err);
  }
};

/**
 * Soft delete del grupo. Solo permitido si no quedan mascotas activas
 * (no soft-deleted) asociadas — Firestore no enforza FK, lo chequeamos acá.
 */
export const softDeleteFeedingGroup = async ({ uid, groupId }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!groupId) throw new AppError('Grupo requerido', 'INVALID_INPUT');

  try {
    const petsRef = collection(db, COLLECTIONS.PETS);
    const q = query(petsRef, where('feedingGroupId', '==', groupId));
    const snap = await getDocs(q);
    const activePets = snap.docs.filter((d) => !d.data().deletedAt);
    if (activePets.length > 0) {
      throw new AppError(
        `El grupo tiene ${activePets.length} mascota${activePets.length === 1 ? '' : 's'} activa${activePets.length === 1 ? '' : 's'}. Movelas a otro grupo primero.`,
        'GROUP_HAS_ACTIVE_PETS'
      );
    }

    const groupRef = doc(db, COLLECTIONS.FEEDING_GROUPS, groupId);
    await updateDoc(groupRef, {
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    throw new FirestoreError('No se pudo eliminar el grupo', err);
  }
};

// =====================================================================
// READS
// =====================================================================

export const fetchFeedingGroup = async (groupId) => {
  if (!groupId) throw new AppError('Grupo requerido', 'INVALID_INPUT');
  try {
    const snap = await getDoc(doc(db, COLLECTIONS.FEEDING_GROUPS, groupId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (err) {
    throw new FirestoreError('No se pudo cargar el grupo', err);
  }
};

export const subscribeToFeedingGroups = (familyId, onNext, onError) => {
  if (!familyId) {
    onError?.(new AppError('familyId requerido', 'INVALID_INPUT'));
    return () => {};
  }
  const groupsRef = collection(db, COLLECTIONS.FEEDING_GROUPS);
  const q = query(groupsRef, where('familyId', '==', familyId));

  return onSnapshot(
    q,
    (snapshot) => {
      const groups = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((g) => !g.deletedAt);
      onNext(groups);
    },
    (err) => {
      onError?.(new FirestoreError('No se pudieron cargar los grupos', err));
    }
  );
};

// =====================================================================
// DISPENSERS — endpoints individuales (Stage 2B)
// =====================================================================

/**
 * Agrega un dispenser nuevo al grupo. Asigna id automáticamente.
 * Stock inicial opcional (default 0). Respeta el cap MAX_DISPENSERS_PER_GROUP.
 *
 * @returns {object} { dispenserId }
 */
export const addDispenser = async ({ uid, feedingGroupId, name, capacityGr, stockGr = 0 }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!feedingGroupId) throw new AppError('Grupo requerido', 'INVALID_INPUT');

  validateDispenser({ name, capacityGr, stockGr });

  try {
    const result = await runTransaction(db, async (transaction) => {
      const groupRef = doc(db, COLLECTIONS.FEEDING_GROUPS, feedingGroupId);
      const snap = await transaction.get(groupRef);
      if (!snap.exists()) throw new AppError('El grupo no existe', 'GROUP_NOT_FOUND');
      const group = snap.data();
      if (group.deletedAt) throw new AppError('El grupo fue eliminado', 'GROUP_DELETED');

      const current = group.dispensers ?? [];
      if (current.length >= VALIDATION.MAX_DISPENSERS_PER_GROUP) {
        throw new AppError(
          `Máximo ${VALIDATION.MAX_DISPENSERS_PER_GROUP} dispensers por grupo`,
          'TOO_MANY_DISPENSERS'
        );
      }

      const newDispenser = {
        id: generateDispenserId(),
        name: name.trim(),
        capacityGr,
        stockGr,
      };

      transaction.update(groupRef, {
        dispensers: [...current, newDispenser],
        updatedAt: serverTimestamp(),
      });

      // Log a nivel de grupo (NOTE con reason específico).
      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: group.familyId,
        feedingGroupId,
        petId: null,
        userId: uid,
        type: LOG_TYPES.NOTE,
        timestamp: serverTimestamp(),
        payload: {
          reason: 'dispenser_added',
          dispenserId: newDispenser.id,
          dispenserName: newDispenser.name,
          capacityGr,
          initialStockGr: stockGr,
        },
      });

      return { dispenserId: newDispenser.id };
    });
    return result;
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo agregar el dispenser', err);
  }
};

/**
 * Elimina un dispenser del grupo. Si tenía stock, se mueve a mainStock
 * (cero pérdida contable). Si era el último dispenser y había activeCycle,
 * el ciclo se cierra (mismo principio que la transición usesDispenser ON→OFF
 * en Stage 1).
 *
 * No permite eliminar el dispenser si el grupo quedaría con 0 y aún hay
 * dispensers que el caller no quiere borrar — eso es decisión consciente
 * del caller. Si quiere "modo sin dispenser", debe llamar removeDispenser
 * para cada uno.
 */
export const removeDispenser = async ({ uid, feedingGroupId, dispenserId }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!feedingGroupId) throw new AppError('Grupo requerido', 'INVALID_INPUT');
  if (!dispenserId) throw new AppError('Dispenser requerido', 'INVALID_INPUT');

  try {
    await runTransaction(db, async (transaction) => {
      const groupRef = doc(db, COLLECTIONS.FEEDING_GROUPS, feedingGroupId);
      const snap = await transaction.get(groupRef);
      if (!snap.exists()) throw new AppError('El grupo no existe', 'GROUP_NOT_FOUND');
      const group = snap.data();
      if (group.deletedAt) throw new AppError('El grupo fue eliminado', 'GROUP_DELETED');

      const current = group.dispensers ?? [];
      const target = current.find((d) => d.id === dispenserId);
      if (!target) {
        throw new AppError('El dispenser no existe en este grupo', 'DISPENSER_NOT_FOUND');
      }

      const movedGr = target.stockGr ?? 0;
      const remaining = current.filter((d) => d.id !== dispenserId);

      const updates = {
        dispensers: remaining,
        updatedAt: serverTimestamp(),
      };
      if (movedGr > 0) {
        updates.mainStockGr = (group.mainStockGr ?? 0) + movedGr;
      }

      // Si quedan 0 dispensers y había ciclo activo, lo cerramos
      // (el grupo pasa a "modo sin dispenser" y el ciclo se vuelve incoherente).
      let learningResult = null;
      if (remaining.length === 0 && group.activeCycle) {
        const closed = closeCycle(group.activeCycle, new Date());
        learningResult = incorporateClosedCycle(closed, {
          cycles: group.cycles || [],
          avgFeedingsPerRefill: group.avgFeedingsPerRefill ?? null,
          avgRefillsPerBag: group.avgRefillsPerBag ?? null,
          cyclesCompleted: group.cyclesCompleted ?? 0,
        });
        updates.cycles = learningResult.cycles;
        updates.avgFeedingsPerRefill = learningResult.avgFeedingsPerRefill;
        updates.avgRefillsPerBag = learningResult.avgRefillsPerBag;
        updates.cyclesCompleted = learningResult.cyclesCompleted;
        updates.learningStatus = learningResult.learningStatus;
        updates.activeCycle = null;
      }

      transaction.update(groupRef, updates);

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: group.familyId,
        feedingGroupId,
        petId: null,
        userId: uid,
        type: LOG_TYPES.NOTE,
        timestamp: serverTimestamp(),
        payload: {
          reason: 'dispenser_removed',
          dispenserId,
          dispenserName: target.name,
          stockMovedGr: movedGr,
          remainingDispensers: remaining.length,
          cycleClosed: Boolean(learningResult),
        },
      });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo eliminar el dispenser', err);
  }
};

/**
 * Cambia el nombre de un dispenser. Operación cosmética.
 */
export const renameDispenser = async ({ uid, feedingGroupId, dispenserId, name }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!feedingGroupId) throw new AppError('Grupo requerido', 'INVALID_INPUT');
  if (!dispenserId) throw new AppError('Dispenser requerido', 'INVALID_INPUT');
  if (!name?.trim()) {
    throw new AppError('El nombre del dispenser es requerido', 'INVALID_DISPENSER_NAME');
  }
  if (name.length > VALIDATION.DISPENSER_NAME_MAX) {
    throw new AppError(
      `El nombre del dispenser supera los ${VALIDATION.DISPENSER_NAME_MAX} caracteres`,
      'INVALID_DISPENSER_NAME'
    );
  }

  try {
    await runTransaction(db, async (transaction) => {
      const groupRef = doc(db, COLLECTIONS.FEEDING_GROUPS, feedingGroupId);
      const snap = await transaction.get(groupRef);
      if (!snap.exists()) throw new AppError('El grupo no existe', 'GROUP_NOT_FOUND');
      const group = snap.data();
      if (group.deletedAt) throw new AppError('El grupo fue eliminado', 'GROUP_DELETED');

      const current = group.dispensers ?? [];
      const target = current.find((d) => d.id === dispenserId);
      if (!target) {
        throw new AppError('El dispenser no existe en este grupo', 'DISPENSER_NOT_FOUND');
      }

      const trimmed = name.trim();
      const updated = current.map((d) => (d.id === dispenserId ? { ...d, name: trimmed } : d));

      transaction.update(groupRef, {
        dispensers: updated,
        updatedAt: serverTimestamp(),
      });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo renombrar el dispenser', err);
  }
};

/**
 * Cambia la capacidad de un dispenser existente.
 * Rechaza si la nueva capacidad es menor que el stock actual (igual al
 * principio histórico de updateFeedingGroup en Stage 1).
 */
export const resizeDispenser = async ({ uid, feedingGroupId, dispenserId, capacityGr }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!feedingGroupId) throw new AppError('Grupo requerido', 'INVALID_INPUT');
  if (!dispenserId) throw new AppError('Dispenser requerido', 'INVALID_INPUT');
  if (
    !Number.isInteger(capacityGr) ||
    capacityGr < VALIDATION.DISPENSER_CAPACITY_MIN_GR ||
    capacityGr > VALIDATION.DISPENSER_CAPACITY_MAX_GR
  ) {
    throw new AppError(
      `La capacidad del dispenser debe ser un entero entre ${VALIDATION.DISPENSER_CAPACITY_MIN_GR} y ${VALIDATION.DISPENSER_CAPACITY_MAX_GR} g`,
      'INVALID_DISPENSER_CAPACITY'
    );
  }

  try {
    await runTransaction(db, async (transaction) => {
      const groupRef = doc(db, COLLECTIONS.FEEDING_GROUPS, feedingGroupId);
      const snap = await transaction.get(groupRef);
      if (!snap.exists()) throw new AppError('El grupo no existe', 'GROUP_NOT_FOUND');
      const group = snap.data();
      if (group.deletedAt) throw new AppError('El grupo fue eliminado', 'GROUP_DELETED');

      const current = group.dispensers ?? [];
      const target = current.find((d) => d.id === dispenserId);
      if (!target) {
        throw new AppError('El dispenser no existe en este grupo', 'DISPENSER_NOT_FOUND');
      }

      if (capacityGr < (target.stockGr ?? 0)) {
        throw new AppError(
          `La nueva capacidad (${capacityGr} g) es menor que el stock actual (${target.stockGr} g). Vaciá el dispenser primero.`,
          'CAPACITY_BELOW_STOCK'
        );
      }

      const updated = current.map((d) => (d.id === dispenserId ? { ...d, capacityGr } : d));

      transaction.update(groupRef, {
        dispensers: updated,
        updatedAt: serverTimestamp(),
      });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo cambiar la capacidad del dispenser', err);
  }
};
